import React from 'react';
import PropTypes from 'prop-types';
import { ContainerQuery } from 'react-container-query';
import { entries, isEmpty, isEqual, map, memoize, noop, uniq } from 'lodash';
// CardsModule should be imported though dist/src instead esm to be included in vod-player chunk
// otherwise it imports playable>hls with side effect breaking lightbox player
import { CardsModule } from '@wix/wix-vod-cards';
import { AnnotoModule } from '@wix/wix-vod-annoto';
import { SettingsModule } from '@wix/wix-vod-playable-settings';
import { VODHlsAdapter } from './adapters/hls';
import { EngineState } from 'playable/dist/src/constants';
import ReactPlayable from 'react-playable';
import { PlayerAPIFacade } from './player-api-facade';
import { encodeHTML } from '../../common/utils/sanitizer';
VODHlsAdapter.DEFAULT_HLS_CONFIG = {
    ...VODHlsAdapter.DEFAULT_HLS_CONFIG,
    debug: true,
    startLevel: -1, // https://github.com/video-dev/hls.js/blob/master/docs/API.md#testbandwidth
    // hls.js default only retries a manifest network error once (1s delay) before going fatal (WV-1766) - widen the budget.
    manifestLoadPolicy: {
        default: {
            maxTimeToFirstByteMs: Infinity, // same as hls.js default
            maxLoadTimeMs: 40000, // hls.js default is 20000
            timeoutRetry: {
                maxNumRetry: 2, // same as hls.js default
                retryDelayMs: 0, // same as hls.js default
                maxRetryDelayMs: 0, // same as hls.js default
            },
            errorRetry: {
                maxNumRetry: 4, // hls.js default is 1
                retryDelayMs: 1000, // same as hls.js default
                maxRetryDelayMs: 8000, // same as hls.js default
            },
        },
    },
    // give fragment requests more headroom before a slow TTFB counts against the load timeout (WV-1766).
    fragLoadPolicy: {
        default: {
            maxTimeToFirstByteMs: 20000, // hls.js default is 10000
            maxLoadTimeMs: 120000, // same as hls.js default
            timeoutRetry: {
                maxNumRetry: 4, // same as hls.js default
                retryDelayMs: 0, // same as hls.js default
                maxRetryDelayMs: 0, // same as hls.js default
            },
            errorRetry: {
                maxNumRetry: 6, // same as hls.js default
                retryDelayMs: 1000, // same as hls.js default
                maxRetryDelayMs: 8000, // same as hls.js default
            },
        },
    },
};
const findChangedProps = (currentProps, previousProps) => {
    const uniqKeys = uniq([
        ...Object.keys(currentProps),
        ...Object.keys(previousProps),
    ]);
    return uniqKeys
        .filter((key) => key !== 'children') // children is handled by React so we can skip it
        .reduce((acc, key) => {
        if (!isEqual(currentProps[key], previousProps[key])) {
            return { ...acc, [key]: currentProps[key] };
        }
        return acc;
    }, {});
};
const makeConfigurationChanger = (handlers) => (changes) => {
    entries(changes).forEach(([propName, propValue]) => {
        if (handlers[propName]) {
            handlers[propName](propValue);
        }
    });
};
const containerQueries = {
    'max-1900px': {
        maxWidth: 1900,
    },
    'min-981px': {
        minWidth: 981,
    },
    'max-639px': {
        maxWidth: 639,
    },
    'min-601px': {
        minWidth: 601,
    },
    'min-579px': {
        minWidth: 579,
    },
    'max-550px': {
        maxWidth: 550,
    },
    'min-460px': {
        minWidth: 460,
    },
    'max-400px': {
        maxWidth: 400,
    },
    'max-350px': {
        maxWidth: 350,
    },
    'min-350px': {
        minWidth: 350,
    },
    'max-300px': {
        maxWidth: 300,
    },
    'max-280px': {
        maxWidth: 280,
    },
};
const getWidthAttribute = (sizes, minOrMax) => Object.keys(sizes)
    .filter((key) => key.startsWith(minOrMax) && sizes[key])
    .map((key) => key.replace(`${minOrMax}-`, ''))
    .join(' ');
export class VODPlayer extends React.Component {
    constructor() {
        super();
        /**
         * ReactPlayable will do shallow compare of it's props,
         * so we need to persist this array
         */
        this.convertToPlayableSrc = memoize((urls) => map(urls, ({ url, type }) => ({
            url,
            type,
        })));
        this.initSettingsModule = (playerInstance) => {
            const { settingsModuleConfig } = this.props;
            const onPopoverVisibilityChange = settingsModuleConfig?.onPopoverVisibilityChange;
            const config = {
                ...settingsModuleConfig,
                onPopoverVisibilityChange(visible) {
                    if (visible) {
                        playerInstance.hideCardsContainer();
                    }
                    else if (playerInstance.showCardsContainer) {
                        playerInstance.showCardsContainer();
                    }
                    if (onPopoverVisibilityChange) {
                        onPopoverVisibilityChange(visible);
                    }
                },
            };
            playerInstance.initSettings(config);
        };
        this.onInit = (playerInstance) => {
            const { isRTL, showSettings } = this.props;
            this.player = playerInstance;
            if (showSettings) {
                this.initSettingsModule(playerInstance);
            }
            playerInstance.setRtl(isRTL);
        };
        this.handleVideoViewMode = (mode) => {
            this.player.setVideoViewMode(mode);
        };
        this.handleLoop = (isLoop) => {
            this.player.setLoop(isLoop);
        };
        this.handleMuted = (muted) => {
            if (muted) {
                this.player.mute();
            }
            else {
                this.player.unmute();
            }
        };
        this.handlePlaysInline = (isInline) => {
            this.player.setPlaysinline(isInline);
        };
        this.handlePlayableChanges = (targetProps) => {
            const changes = findChangedProps(this.props, targetProps);
            if (!isEmpty(changes)) {
                this.changePlayerUI(changes);
            }
            return changes;
        };
        this.handlePauseVideoOnFullScreenExit = (isMobile) => {
            if (isMobile) {
                this.player.enablePauseVideoOnFullScreenExit();
            }
            else {
                this.player.disablePauseVideoOnFullScreenExit();
            }
        };
        this.handlePlaybackStateChanged = (playbackState) => {
            const { onPlay, onPause, onEnded } = this.props;
            const handlers = {
                [EngineState.PLAYING]: onPlay,
                [EngineState.PAUSED]: onPause,
                [EngineState.ENDED]: onEnded,
            };
            if (handlers[playbackState]) {
                handlers[playbackState]();
            }
        };
        this.handleLogoProperties = (logo) => {
            if (!logo) {
                this.player.setLogo();
                return;
            }
            this.player.setLogo(logo.src);
            this.player.setAlwaysShowLogo(logo.showAlways);
        };
        this.handlePlayAtFullScreen = (playAtFullScreen) => {
            if (playAtFullScreen) {
                this.player.enableEnterFullScreenOnPlay();
            }
            else {
                this.player.disableEnterFullScreenOnPlay();
            }
        };
        this.handleSetMainUIShouldAlwaysShow = (setMainUIShouldAlwaysShow) => {
            this.player.setMainUIShouldAlwaysShow(setMainUIShouldAlwaysShow);
        };
        this.handleOnTitleClick = (onTitleClick) => this.player.setTitleClickCallback(onTitleClick);
        this.handleCanDownload = (canDownload) => {
            const { onDownloadClick } = this.props;
            if (onDownloadClick && canDownload) {
                this.player.setDownloadClickCallback(onDownloadClick);
                this.player.showDownloadButton();
                return;
            }
            this.player.setDownloadClickCallback();
            this.player.hideDownloadButton();
        };
        this.handleOnWatchOnSiteClick = (onWatchOnSiteClick) => {
            this.player.setLogoClickCallback(onWatchOnSiteClick);
        };
        this.handlePlaying = ({ isVideoPlayRequested, vidiUrls, isVideoPauseRequested, }) => {
            const shouldTriggerPlay = isEmpty(vidiUrls) ? false : isVideoPlayRequested;
            if (shouldTriggerPlay && isVideoPauseRequested) {
                console.warn('VODPlayer: isVideoPlayRequested and isVideoPauseRequested both true');
                return;
            }
            if (shouldTriggerPlay) {
                this.player.play();
            }
            if (isVideoPauseRequested) {
                this.player.pause();
            }
        };
        this.makePlayableConfig = ({ isAnnotoModuleEnabled, disableFullScreen, hideOverlay, modules, hideMainUI, disableControlWithKeyboard, }) => ({
            modules: {
                cards: CardsModule,
                settings: SettingsModule,
                ...(isAnnotoModuleEnabled && { annoto: AnnotoModule }),
                ...modules,
            },
            disableFullScreen,
            playbackAdapters: [VODHlsAdapter],
            hideOverlay,
            hideMainUI,
            disableControlWithKeyboard,
        });
        this.player = null;
        this.changePlayerUI = makeConfigurationChanger({
            logo: this.handleLogoProperties,
            playAtFullScreen: this.handlePlayAtFullScreen,
            onTitleClick: this.handleOnTitleClick,
            canDownload: this.handleCanDownload,
            onWatchOnSiteClick: this.handleOnWatchOnSiteClick,
            setMainUIShouldAlwaysShow: this.handleSetMainUIShouldAlwaysShow,
            mobileMode: this.handlePauseVideoOnFullScreenExit,
            muted: this.handleMuted,
            playInline: this.handlePlaysInline,
            loop: this.handleLoop,
            videoViewMode: this.handleVideoViewMode,
        });
        this.convertToPlayableSrc.cache = new Map();
    }
    componentDidMount() {
        this.changePlayerUI(this.props);
        this.handlePlaying(this.props);
        this.props.onInit(this.player);
    }
    componentDidUpdate(prevProps) {
        const changes = this.handlePlayableChanges(prevProps);
        if (changes.isVideoPlayRequested || changes.isVideoPauseRequested) {
            this.handlePlaying(this.props);
        }
    }
    render() {
        const { children, onUserSeek, onSeek, onMuteChange, onVolumeChange, vidiUrls, autoPlay, preload, fillAllSpace, videoItem, canShowTitle, texts, width, height, posterUrl, onPlayAbort, onError, onFullScreenChanged, } = this.props;
        const config = this.makePlayableConfig(this.props);
        const src = this.convertToPlayableSrc(vidiUrls);
        const style = fillAllSpace
            ? {
                width: '100%',
                height: '100%',
            }
            : {
                width,
                height,
            };
        return (React.createElement(ContainerQuery, { query: containerQueries }, (sizes) => (React.createElement("div", { style: style, "data-vod-player-max-width": getWidthAttribute(sizes, 'max'), "data-vod-player-min-width": getWidthAttribute(sizes, 'min') },
            React.createElement(ReactPlayable, { width: width, height: height, fillAllSpace: fillAllSpace, src: src, autoplay: autoPlay, preload: preload, title: canShowTitle ? encodeHTML(videoItem.title) : null, poster: posterUrl, texts: texts, config: config, onInit: this.onInit },
                React.createElement(PlayerAPIFacade, { onFullScreenChanged: onFullScreenChanged, onPlaybackStateChanged: this.handlePlaybackStateChanged, onUserSeek: onUserSeek, onSeek: onSeek, onMute: onMuteChange, onVolumeChange: onVolumeChange, onPlayAbort: onPlayAbort, onError: onError }, children))))));
    }
}
VODPlayer.propTypes = {
    children: PropTypes.node,
    vidiUrls: PropTypes.array,
    className: PropTypes.string,
    preload: PropTypes.string,
    videoViewMode: PropTypes.string,
    posterUrl: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    onInit: PropTypes.func,
    onUserSeek: PropTypes.func,
    onSeek: PropTypes.func,
    onPlay: PropTypes.func,
    onPause: PropTypes.func,
    onEnded: PropTypes.func,
    onPlayAbort: PropTypes.func,
    onFullScreenChanged: PropTypes.func,
    onAnomalyDetected: PropTypes.func,
    onError: PropTypes.func,
    onMuteChange: PropTypes.func,
    onVolumeChange: PropTypes.func,
    onTitleClick: PropTypes.func,
    onDownloadClick: PropTypes.func,
    onWatchOnSiteClick: PropTypes.func,
    setMainUIShouldAlwaysShow: PropTypes.bool,
    muted: PropTypes.bool,
    autoPlay: PropTypes.bool,
    loop: PropTypes.bool,
    canShowTitle: PropTypes.bool,
    canDownload: PropTypes.bool,
    isAnnotoModuleEnabled: PropTypes.bool,
    disableFullScreen: PropTypes.bool,
    hideOverlay: PropTypes.bool,
    hideMainUI: PropTypes.bool,
    isVideoPlayRequested: PropTypes.bool,
    isVideoPauseRequested: PropTypes.bool,
    mobileMode: PropTypes.bool,
    playAtFullScreen: PropTypes.bool,
    playInline: PropTypes.bool,
    loader: PropTypes.bool,
    isRTL: PropTypes.bool,
    fillAllSpace: PropTypes.bool,
    showSettings: PropTypes.bool,
    texts: PropTypes.object,
    modules: PropTypes.object,
    channelData: PropTypes.object,
    videoItem: PropTypes.object,
    // import type { SettingsModuleConfig } from '@wix/wix-vod-playable-settings';
    settingsModuleConfig: PropTypes.object,
    disableControlWithKeyboard: PropTypes.bool,
};
VODPlayer.defaultProps = {
    width: 640,
    height: 360,
    mobileMode: false,
    logo: null,
    preload: 'metadata',
    canShowTitle: true,
    onPause: noop,
    onPlay: noop,
    onEnded: noop,
    onPlayAbort: noop,
    onVideoInfoShow: noop,
    onBuyRentButtonClick: noop,
    onShareVideoShow: noop,
    onFullScreenChanged: noop,
    onWatchOnSiteClick: noop,
    onMuteChange: noop,
    onVolumeChange: noop,
    onSeek: noop,
    onUserSeek: noop,
    onError: noop,
    onInit: noop,
    onAnomalyDetected: noop,
    isAnnotoEnabled: false,
    disableFullScreen: false,
    hideOverlay: true,
    showSettings: false,
    // From Playable documentation:
    // `config.disableControlWithKeyboard: Boolean` Pass `false` to disable ability to control player through keyboard
    // NOTE: by default React-Playable disables controls with keyboard
    // we set true to ENABLE CONTROLS WITH KEYBOARD
    disableControlWithKeyboard: true,
};
//# sourceMappingURL=vod-player.js.map